import i18next from "i18next";

import { DEFAULT_MIVO_LOCALE, type MivoTranslationKey } from "./ITranslation";
import {
  type MarketCountry,
  isMarketCountry,
} from "./marketCountry";
import { resolveInitialMarketCountry, writeStoredMarketCountry } from "./marketCountryStorage";
import { tryParseLocaleParam } from "./localeNormalization";
import { isMivoTranslationKey, normalizeStoredLocale, writeStoredLocale } from "./localeStorage";

export function readLocaleFromSearch(search?: string): MivoTranslationKey | null {
  const params = new URLSearchParams(
    search ?? (typeof window !== "undefined" ? window.location.search : ""),
  );
  const value = params.get("locale");
  if (!value) {
    return null;
  }
  return tryParseLocaleParam(value);
}

export function stripLocaleFromUrl(): void {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  if (!url.searchParams.has("locale") && !url.searchParams.has("marketCountry")) {
    return;
  }

  url.searchParams.delete("locale");
  url.searchParams.delete("marketCountry");
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, "", next);
}

export function readMarketCountryFromSearch(search?: string): MarketCountry | null {
  const params = new URLSearchParams(
    search ?? (typeof window !== "undefined" ? window.location.search : ""),
  );
  const value = params.get("marketCountry");
  return isMarketCountry(value) ? value : null;
}

function currentLocale(): MivoTranslationKey {
  return isMivoTranslationKey(i18next.language) ? i18next.language : DEFAULT_MIVO_LOCALE;
}

/** Apply valid `?locale=` on boot, persist, and strip param (FR-13, E-09). */
export function bootstrapLocaleFromUrl(): MivoTranslationKey {
  if (typeof window === "undefined") {
    return currentLocale();
  }

  const fromUrl = readLocaleFromSearch();
  stripLocaleFromUrl();

  if (fromUrl) {
    void i18next.changeLanguage(fromUrl);
    writeStoredLocale(fromUrl);
    return fromUrl;
  }

  return currentLocale();
}

export function bootstrapMarketCountryFromUrl(): MarketCountry {
  if (typeof window === "undefined") {
    return resolveInitialMarketCountry();
  }

  const fromUrl = readMarketCountryFromSearch();
  stripLocaleFromUrl();

  if (fromUrl) {
    writeStoredMarketCountry(fromUrl);
    return fromUrl;
  }

  return resolveInitialMarketCountry();
}
